import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';

import { EXT_DATE_TIME_MILLISECS_FORMAT, TITLE_DATE_TIME_FORMAT } from '../constants';

import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';

import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';

import {
   ChartWrapper,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../../styledComponents/styledComponents';
import { CardTitle, Col, Row } from 'reactstrap';
import { showAlwaysOverflowTooltip } from '../../../common/OverflowTooltip/OverflowTooltip';
import { CsvDataExportButton } from '../../../common';
import { useSelector } from 'react-redux';
import { troubleModeSelector } from '../../../../redux/selectors/selectors';
import { E2E } from '../../../../constants/general';

const columns = [
   {
      text: 'hiddenKey',
      dataField: 'hiddenKey',
      hidden: 'true',
   },
   {
      text: 'Drop type',
      dataField: 'type',
      headerStyle: { fontSize: '13px' },
      formatter: (cell) => showAlwaysOverflowTooltip(cell),
   },
   {
      text: 'Date',
      dataField: 'date',
      headerStyle: { fontSize: '13px' },
      formatter: (cell) => showAlwaysOverflowTooltip(cell),
   },
];

function addHiddenKeyColumn(rows) {
   return rows.map((row, idx) => ({ ...row, hiddenKey: idx }));
}

function DropTable({ height, drops, start, end, streamName }) {
   const mode = useSelector(troubleModeSelector);

   const startFormatted = start.format(TITLE_DATE_TIME_FORMAT);
   const endFormatted = end.format(TITLE_DATE_TIME_FORMAT);

   const cutDrops =
      drops.length <= 20
         ? drops
         : drops.filter((drop) => {
              const dropMoment = moment.utc(drop.date, EXT_DATE_TIME_MILLISECS_FORMAT);
              return dropMoment.diff(start) >= 0 && end.diff(dropMoment) >= 0;
           });

   const jsonCsvExportData = useMemo(() => {
      return cutDrops.map((drop) => {
         const ts = moment.utc(drop.date, EXT_DATE_TIME_MILLISECS_FORMAT);
         return {
            timestamp_as_string: ts.format(),
            timestamp: ts.unix(),
            dropType: drop.type,
         };
      });
   }, [cutDrops]);

   const options = useMemo(() => {
      return {
         start: start.unix(),
         end: end.unix(),
         streamNames: [streamName],
      };
   }, [start, end, streamName]);

   return (
      <PaddingContainer $padding={'0px 8px'}>
         <WidgetContainer $margin={'0px -7px 6px -7px'}>
            <WidgetHeaderContainer>
               <HeightSetContainer $heightInPx={23}>
                  <TwoSideFlexContainer>
                     <SimpleFlexRow>
                        <SimpleFlexItem>
                           <CsvDataExportButton
                              jsonData={jsonCsvExportData}
                              filename={'Ingest_drops'}
                              options={options}
                           />
                        </SimpleFlexItem>
                     </SimpleFlexRow>
                     <SimpleFlexRow></SimpleFlexRow>
                  </TwoSideFlexContainer>
               </HeightSetContainer>
            </WidgetHeaderContainer>
            <WidgetBodyContainer>
               <Row>
                  <Col>
                     <CardTitle className='mb-0'>Drops</CardTitle>
                     <div className='small text-muted'>{`From ${startFormatted} to ${endFormatted} \u00A0\u00A0( UTC time )`}</div>
                  </Col>
               </Row>
               <ChartWrapper
                  className='chart-wrapper'
                  height={height}
                  marginTopPx={45}
                  autoScroll={true}
                  paddingRightPx={4}
               >
                  <ToolkitProvider
                     keyField='hiddenKey'
                     data={addHiddenKeyColumn(cutDrops)}
                     columns={columns}
                     bootstrap4
                  >
                     {(props) => (
                        <>
                           <BootstrapTable
                              {...props.baseProps}
                              pagination={paginationFactory({
                                 sizePerPage: 5,
                                 hideSizePerPage: true,
                              })}
                              striped
                              hover
                              id={'CustomTable-drops'}
                              classes={`table-responsive-${mode === E2E ? 'lg' : ''}`}
                           />
                        </>
                     )}
                  </ToolkitProvider>
               </ChartWrapper>
            </WidgetBodyContainer>
         </WidgetContainer>
      </PaddingContainer>
   );
}

DropTable.propTypes = {
   drops: PropTypes.array,
   height: PropTypes.number,
   start: PropTypes.object,
   end: PropTypes.object,
   streamName: PropTypes.string,
};

DropTable.defaultProps = {
   height: 295,
};
export default DropTable;
